import { Box, Typography } from '@mui/material';
import { ArrowLeft, Clock, Package, ShoppingCart } from 'lucide-react';
import { BackButton } from '../../Styles/AppStyles';
import { formatRelativeTime } from '../../Services/FormatRelativeTime';
import { COLORS } from '../../Styles/Colors';
import { VendingMachineDetail } from '../../Types/vending';
import { MachineListStatusIndicator, MachineStatusChip } from './MachineStatusChip';
import { MetaTimestampGrid, MetaTimestampRow } from './VendingStyles';

interface MachineDetailHeaderProps {
  machine: VendingMachineDetail;
  onBack: () => void;
}

const MetaItem = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) => (
  <MetaTimestampRow>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: COLORS.lightGray }}>
      {icon}
      <span>{label}</span>
    </Box>
    <Box component="span" sx={{ fontWeight: 600, color: COLORS.darkGray, textAlign: 'right' }}>
      {value}
    </Box>
  </MetaTimestampRow>
);

export const MachineDetailHeader = ({ machine, onBack }: MachineDetailHeaderProps) => {
  const displayName = machine.machine_name || `VM-${machine.provider_machine_id}`;
  const customer = machine.customer_name
    || (machine.location_map_status === 'mapped' ? machine.customer_label : null)
    || 'Unassigned';
  const location = machine.location_name
    || [machine.city, machine.state].filter(Boolean).join(', ')
    || 'Unassigned';

  return (
    <Box
      sx={{
        px: { xs: 2, md: 3 },
        pt: { xs: 1.25, md: 3 },
        pb: 2.5,
        backgroundColor: '#f8f9fa',
        flexShrink: 0,
        borderBottom: `1px solid ${COLORS.lightGray}40`,
      }}
    >
      <Box sx={{ mb: 2 }}>
        <BackButton onClick={onBack}>
          <ArrowLeft size={16} />
          Machines
        </BackButton>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2.5,
        }}
      >
        <Box sx={{ minWidth: 0, flex: '1 1 280px' }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Typography
              variant="h4"
              fontWeight="bold"
              component="h1"
              sx={{ color: COLORS.teal, fontSize: { xs: '1.5rem', md: '2rem' } }}
            >
              {displayName}
            </Typography>
            <MachineListStatusIndicator
              connection_status={machine.connection_status}
              status={machine.status}
              open_alerts_count={machine.open_alerts_count}
              empty_slots={machine.empty_slots}
              low_slots={machine.low_slots}
            />
            <MachineStatusChip status={machine.status} />
          </Box>
          <Typography variant="h6" color={COLORS.darkGray} fontWeight={700} sx={{ mb: 0.25 }}>
            {customer}
          </Typography>
          <Typography variant="body1" color={COLORS.lightGray}>
            {location}
          </Typography>
          {machine.inventory_health_pct != null && (
            <Typography
              variant="body2"
              sx={{ mt: 1, color: COLORS.teal, fontWeight: 600 }}
            >
              Inventory health: {machine.inventory_health_pct}%
            </Typography>
          )}
        </Box>

        <MetaTimestampGrid>
          <MetaItem
            icon={<Clock size={14} />}
            label="Last communication"
            value={formatRelativeTime(machine.last_communication)}
          />
          <MetaItem
            icon={<Package size={14} />}
            label="Last inventory update"
            value={formatRelativeTime(machine.last_inventory_sync)}
          />
          <MetaItem
            icon={<ShoppingCart size={14} />}
            label="Last vend"
            value={formatRelativeTime(machine.last_vend_at)}
          />
        </MetaTimestampGrid>
      </Box>
    </Box>
  );
};

export default MachineDetailHeader;
